import type { Client, EntityType, MetricValue } from '@ops/contracts'
import { useState } from 'react'

import { CargarDatoDialog, CrearSeguimientoDialog } from '../comunes'
import type { Frente } from './frentes'
import { MetricaDrawer } from './MetricaDrawer'
import { useMetricMeta, type MetricMeta } from './metricMeta'

/**
 * Estado compartido de las tres variantes del Overview: el reloj de la pantalla,
 * los metadatos del catálogo de métricas y los tres diálogos.
 *
 * El reloj se congela una vez por montaje. Si cada badge de frescura llamara a
 * `new Date()`, dos tarjetas de la misma pantalla podrían discrepar en el borde
 * de un día y una diría "hace 7 días" y la otra "hace 8".
 */
/**
 * Sobre qué se abre el diálogo de seguimiento. Es del Overview y no del diálogo
 * compartido: acá el objetivo se arma de un frente, de una cuenta o de una
 * métrica, y cada uno redacta su propia sugerencia.
 */
export interface SeguimientoObjetivo {
  entityType: EntityType
  entityId: string
  /** Título sugerido, armado del frente que lo originó. */
  sugerencia: string
  /** Contexto del frente, para el cuerpo del work item. */
  contexto: string
  /** Nombre de la entidad, para el subtítulo del diálogo. */
  nombre: string
}

export interface Tablero {
  now: Date
  meta: MetricMeta
  /** Abre el diálogo de seguimiento con el título sugerido del frente. */
  seguirFrente: (frente: Frente) => void
  seguirCliente: (cliente: Client) => void
  seguirMetrica: (metric: MetricValue) => void
  cargarDato: (metric: MetricValue) => void
  verDetalle: (metric: MetricValue) => void
  /** Props ya armadas para `<TableroDialogos />`. */
  dialogos: DialogosState
}

export interface DialogosState {
  objetivo: SeguimientoObjetivo | null
  aCargar: MetricValue | null
  detalle: MetricValue | null
  cerrarObjetivo: () => void
  cerrarCargar: () => void
  cerrarDetalle: () => void
}

export function useTablero(periodo: string): Tablero {
  const [now] = useState(() => new Date())
  const meta = useMetricMeta(periodo)

  const [objetivo, setObjetivo] = useState<SeguimientoObjetivo | null>(null)
  const [aCargar, setACargar] = useState<MetricValue | null>(null)
  const [detalle, setDetalle] = useState<MetricValue | null>(null)

  return {
    now,
    meta,
    seguirFrente: (frente) =>
      setObjetivo({
        entityType: frente.entityType,
        entityId: frente.entityId,
        sugerencia: frente.titulo,
        contexto: `${frente.detalle}\n\nAbierto desde el Overview del panel: ${frente.href}`,
        nombre: frente.titulo,
      }),
    seguirCliente: (cliente) =>
      setObjetivo({
        entityType: 'client',
        entityId: cliente.id,
        sugerencia: `Desbloquear ${cliente.nombre}`,
        contexto: `${cliente.nombre} (${cliente.vertical}) lleva ${cliente.diasEnEstado} días en su estado actual.`,
        nombre: cliente.nombre,
      }),
    seguirMetrica: (metric) =>
      setObjetivo({
        entityType: 'metric',
        entityId: metric.slug,
        sugerencia: `Resolver ${metric.nombre}`,
        contexto:
          metric.valor === null
            ? `${metric.nombre} no tiene dato cargado en ${metric.periodo}.`
            : `${metric.nombre} necesita seguimiento en ${metric.periodo}.`,
        nombre: metric.nombre,
      }),
    cargarDato: setACargar,
    verDetalle: setDetalle,
    dialogos: {
      objetivo,
      aCargar,
      detalle,
      cerrarObjetivo: () => setObjetivo(null),
      cerrarCargar: () => setACargar(null),
      cerrarDetalle: () => setDetalle(null),
    },
  }
}

export interface TableroDialogosProps {
  tablero: Tablero
  periodo: string
}

/** Los tres diálogos de la pantalla, montados una sola vez al final del árbol. */
export function TableroDialogos({ tablero, periodo }: TableroDialogosProps) {
  const { dialogos, meta, now } = tablero
  const detalle = dialogos.detalle
  const objetivo = dialogos.objetivo
  const aCargar = dialogos.aCargar

  return (
    <>
      {objetivo !== null ? (
        <CrearSeguimientoDialog
          open
          onClose={dialogos.cerrarObjetivo}
          entityType={objetivo.entityType}
          entityId={objetivo.entityId}
          tituloInicial={objetivo.sugerencia}
          contexto={objetivo.contexto}
          nombre={objetivo.nombre}
          contextoEditable
          ownerSugerido={
            objetivo.entityType === 'metric'
              ? (meta.ownerDe(objetivo.entityId)?.id ?? null)
              : null
          }
        />
      ) : null}
      {aCargar !== null ? (
        <CargarDatoDialog
          open
          onClose={dialogos.cerrarCargar}
          slug={aCargar.slug}
          nombre={aCargar.nombre}
          // El mes de la métrica y no el de la pantalla: el Overview es el único
          // sitio que deja cargar un período anterior al que se está mirando.
          periodo={aCargar.periodo}
          unidad={aCargar.unidad}
          permitirCambiarPeriodo
          {...(aCargar.benchmark === null
            ? {}
            : { ayudaValor: `Referencia: ${aCargar.benchmark}.` })}
          {...(aCargar.formula === null
            ? {}
            : {
                ficha: [
                  {
                    rotulo: 'Fórmula',
                    valor: (
                      <code className="rounded border border-border bg-n100 px-1.5 py-0.5 font-mono text-micro text-text">
                        {aCargar.formula}
                      </code>
                    ),
                  },
                ],
              })}
        />
      ) : null}
      <MetricaDrawer
        metric={detalle}
        def={detalle === null ? undefined : meta.defDe(detalle.slug)}
        owner={detalle === null ? null : meta.ownerDe(detalle.slug)}
        onClose={dialogos.cerrarDetalle}
        onCargar={(metric) => {
          dialogos.cerrarDetalle()
          tablero.cargarDato(metric)
        }}
        onCrearSeguimiento={(metric) => {
          dialogos.cerrarDetalle()
          tablero.seguirMetrica(metric)
        }}
        now={now}
      />
    </>
  )
}
